'use client';

import { AtSign, ImageIcon, Loader2, Plus, Video, X } from 'lucide-react';

const TypeIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 4v16" />
    <path d="M4 7V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2" />
    <path d="M9 20h6" />
  </svg>
);

import {
  type CSSProperties,
  Fragment,
  type PointerEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useState,
} from 'react';
import { ProductLineElement } from '@/app/components/icons/ProductLineElement';
import { ProductLineVideoReference } from '@/app/components/icons/ProductLineVideoReference';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { TextElementCover } from '@/app/components/shared/TextElementCover';
import { VideoPlayer } from '@/app/components/shared/VideoPlayer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/components/ui/popover';
import { Textarea } from '@/app/components/ui/textarea';
import { MAX_PROMPT_LENGTH } from '@/app/constants';
import type { ElementModality } from '@/app/types';
import { cn } from '@/app/utils';

export type MediaChip = {
  id: string;
  label: string;
  mobileBadge?: string;
  preview?: string;
  videoPreview?: string;
  videoCover?: string;
  type: 'image' | 'video' | 'element';
  elementModality?: ElementModality;
  /** 已入选的社区共享主体，图标用紫色区分于个人主体的蓝色 */
  isShareElement?: boolean;
  loading?: boolean;
  removable?: boolean;
  onRemove?: () => void;
};

/** 卡片展开时是否需要横向平移，以及用哪一段偏移量；不传表示原地展开。 */
export type DesktopMediaStackExpandedOffset = 'video' | 'image' | 'subject';

export type MediaStackAddMenuItem = {
  key: string;
  icon: ReactNode;
  label: ReactNode;
  disabled?: boolean;
  onSelect: () => void;
};

export const getDesktopMediaStackExpandedWidth = (chipCount: number, includeAddCard: boolean) =>
  Math.max(64, (chipCount + (includeAddCard ? 1 : 0)) * 72 - 8);

const getMediaChipIcon = (chip: Pick<MediaChip, 'type' | 'elementModality'>, className?: string) => {
  if (chip.type === 'video' || chip.elementModality === 'video') {
    return <Video className={className} />;
  }

  if (chip.type === 'element') {
    return <AtSign className={className} />;
  }

  return <ImageIcon className={className} />;
};

const MediaChipPreview = ({
  chip,
  className,
  textClassName,
}: {
  chip: MediaChip;
  className?: string;
  textClassName?: string;
}) => {
  const loaderOverlay = chip.loading ? (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/35 backdrop-blur-[1px]">
      <Loader2 className="size-4 animate-spin text-system-white" />
    </div>
  ) : null;

  if (chip.videoPreview) {
    return (
      <div className={cn('relative size-full overflow-hidden', className)}>
        <VideoPlayer
          src={chip.videoPreview}
          poster={chip.videoCover || chip.preview}
          creationId=""
          autoPlay={false}
          playOnHover={true}
          hideControls={true}
          className="size-full overflow-hidden"
          videoClassName="size-full object-cover"
        />
        {loaderOverlay}
      </div>
    );
  }

  if (chip.elementModality === 'text') {
    return (
      <div className={cn('size-full overflow-hidden', className)}>
        <TextElementCover name={chip.label} className="rounded-none" textClassName={textClassName} />
      </div>
    );
  }

  if (chip.preview) {
    return (
      <div className={cn('relative size-full overflow-hidden', className)}>
        <ImageWithPlaceholder src={chip.preview} alt={chip.label} className="size-full object-cover" />
        {loaderOverlay}
      </div>
    );
  }

  if (chip.loading) {
    return (
      <div className={cn('flex size-full items-center justify-center bg-white/[0.04]', className)}>
        <Loader2 className="size-4 animate-spin text-system-white64" />
      </div>
    );
  }

  return (
    <div className={cn('flex size-full items-center justify-center bg-white/[0.04] text-system-white64', className)}>
      {getMediaChipIcon(chip, 'size-5')}
    </div>
  );
};

const ElementChipBadgeIcon = ({ chip }: { chip: MediaChip }) => {
  if (!chip.elementModality || chip.elementModality === 'text') return null;

  const Icon = chip.elementModality === 'video' ? ProductLineVideoReference : ProductLineElement;

  return (
    <span
      className={cn(
        'absolute bottom-1 left-1 z-10 flex size-4 items-center justify-center rounded-full bg-black/70 text-system-blue02 backdrop-blur',
        chip.isShareElement && 'text-[#AB8DFF]',
      )}
    >
      <Icon className="size-[11px]" />
    </span>
  );
};

export const MobileMediaChipItem = ({ chip }: { chip: MediaChip }) => (
  <div className="group relative flex size-[64px] shrink-0 items-center justify-center overflow-hidden rounded-8 bg-white/[0.06] text-system-white">
    <MediaChipPreview chip={chip} className="absolute inset-0 rounded-none" textClassName="text-xs" />
    {chip.removable && chip.onRemove ? (
      <button
        type="button"
        aria-label={chip.label}
        className="-top-1 -right-1 absolute z-[60] flex size-4 shrink-0 items-center justify-center rounded-full bg-black/40 text-system-white80 backdrop-blur-[4px]"
        onClick={(event) => {
          event.stopPropagation();
          chip.onRemove?.();
        }}
      >
        <X className="size-3" />
      </button>
    ) : null}
    {chip.mobileBadge ? (
      <span className="absolute bottom-1 left-1 flex size-4 items-center justify-center rounded-full bg-black/85 font-semibold text-[6px] text-system-white leading-none">
        {chip.mobileBadge}
      </span>
    ) : null}
    <ElementChipBadgeIcon chip={chip} />
  </div>
);

/**
 * 素材卡片上的「+」。给了 menuItems 就先弹下拉让用户选入口（上传 / 主体），
 * 否则保持原来的单击直达行为。
 */
const MediaStackAddButton = ({
  className,
  style,
  ariaLabel,
  disabled,
  onClick,
  menuItems,
  onMenuOpenChange,
  children,
}: {
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
  disabled?: boolean;
  onClick?: () => void;
  menuItems?: MediaStackAddMenuItem[];
  onMenuOpenChange?: (open: boolean) => void;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const button = (
    <button
      type="button"
      aria-label={ariaLabel}
      className={className}
      style={style}
      onClick={(event) => {
        event.stopPropagation();
        if (!menuItems?.length) {
          onClick?.();
        }
      }}
      disabled={disabled}
    >
      {children}
    </button>
  );

  if (!menuItems?.length) {
    return button;
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        onMenuOpenChange?.(nextOpen);
      }}
    >
      <DropdownMenuTrigger asChild={true}>{button}</DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="center"
        sideOffset={8}
        collisionPadding={16}
        className="z-[41] w-[100px] gap-0 overflow-hidden rounded-12 border border-white/[0.08] bg-[rgba(23,25,28,0.64)] p-0 text-system-white shadow-[0_24px_80px_rgba(0,0,0,0.4)] backdrop-blur-[30px]"
      >
        {menuItems.map((item, index) => (
          <Fragment key={item.key}>
            {index > 0 ? <div aria-hidden={true} className="h-px bg-white/[0.12]" /> : null}
            <DropdownMenuItem
              disabled={item.disabled}
              className="flex h-[40px] items-center justify-center gap-2 rounded-none px-3 text-system-white64 text-xs hover:bg-white/[0.08] hover:text-system-white"
              onSelect={() => item.onSelect()}
            >
              {item.icon}
              {item.label}
            </DropdownMenuItem>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const DesktopMediaStackCard = ({
  chips,
  title,
  emptyIcon,
  onClick,
  disabled,
  canAdd,
  onAddClick,
  addDisabled,
  addMenuItems,
  onAddMenuOpenChange,
  clickableWithMedia = false,
  showEmptyTitle = false,
  emptyTitle,
  emptyContent,
  expandedOffset,
  hideCollapsedShadow = false,
  stackExpanded: controlledStackExpanded,
  onStackExpandedChange,
}: {
  chips: MediaChip[];
  title: ReactNode;
  emptyIcon: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  canAdd?: boolean;
  onAddClick?: () => void;
  addDisabled?: boolean;
  addMenuItems?: MediaStackAddMenuItem[];
  onAddMenuOpenChange?: (open: boolean) => void;
  clickableWithMedia?: boolean;
  showEmptyTitle?: boolean;
  emptyTitle?: ReactNode;
  emptyContent?: ReactNode;
  expandedOffset?: DesktopMediaStackExpandedOffset;
  hideCollapsedShadow?: boolean;
  stackExpanded?: boolean;
  onStackExpandedChange?: (expanded: boolean) => void;
}) => {
  const [uncontrolledStackExpanded, setUncontrolledStackExpanded] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const handleAddMenuOpenChange = (open: boolean) => {
    setAddMenuOpen(open);
    onAddMenuOpenChange?.(open);
  };
  const hasMedia = chips.length > 0;
  const hasMultipleMedia = chips.length > 1;

  const showAddButton = hasMedia && canAdd;
  const showExpandedAddCard = hasMultipleMedia && !!showAddButton;
  const stackExpanded = controlledStackExpanded ?? uncontrolledStackExpanded;
  const updateStackExpanded = (expanded: boolean) => {
    // 下拉展开时指针会移到 portal 内容上，这里不能顺势把堆叠收起来，否则加号按钮会被
    // 透明化，菜单就悬在一个看不见的锚点上。
    if (!expanded && addMenuOpen) return;
    if (controlledStackExpanded === undefined) {
      setUncontrolledStackExpanded(expanded);
    }
    onStackExpandedChange?.(expanded);
  };
  const handleStackPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    // An empty reference area must not expand its sibling stack on hover.
    if (!hasMedia) {
      updateStackExpanded(false);
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    updateStackExpanded(!!target?.closest('[data-stack-hover-target="true"], [data-stack-remove-button="true"]'));
  };
  const stackLayerStyles = chips.map(() => ({
    top: '0px',
    left: '0px',
    height: '100%',
    width: '64px',
  }));
  const expandedStackWidth = getDesktopMediaStackExpandedWidth(chips.length, showExpandedAddCard);
  const removableChip = hasMultipleMedia
    ? undefined
    : [...chips].reverse().find((chip) => chip.removable && chip.onRemove);
  const cardIsClickable = !disabled && !!onClick && (!hasMedia || clickableWithMedia);
  const titleText = typeof title === 'string' ? title : undefined;

  return (
    <div
      data-stack-expanded={stackExpanded}
      className={cn(
        'group/stack relative z-0 flex shrink-0 items-start justify-center focus-within:z-20',
        expandedOffset
          ? // 旧布局：固定宽度 + pointer-events-none
            'pointer-events-none h-[82px] w-[64px] data-[stack-expanded=true]:z-20'
          : // 新布局：宽度跟随内容，高度固定
            'h-[82px]',
      )}
      onPointerOver={handleStackPointerMove}
      onPointerMove={handleStackPointerMove}
      onPointerLeave={() => {
        // Q2Pro 使用外层共享 hover 状态；卡片移入分割线/相邻卡片时不应由单卡片抢先收起。
        if (controlledStackExpanded !== undefined && !expandedOffset) return;
        updateStackExpanded(false);
      }}
    >
      <div
        className={cn(
          expandedOffset
            ? // 旧布局：absolute + translate
              'pointer-events-auto absolute top-0 left-0 h-[90px] w-16 overflow-visible transition-[width,transform] duration-200 ease-out group-data-[stack-expanded=true]/stack:w-[var(--reference-stack-expanded-width)]'
            : // 新布局：相对定位，宽度 transition（占据空间）
              'relative h-[90px] w-16 overflow-visible transition-[width] duration-200 ease-out group-data-[stack-expanded=true]/stack:w-[var(--reference-stack-expanded-width)]',
          expandedOffset === 'image' &&
            'group-data-[stack-expanded=true]/stack:translate-x-[var(--reference-image-expanded-offset)]',
          expandedOffset === 'subject' &&
            'group-data-[stack-expanded=true]/stack:translate-x-[var(--reference-subject-expanded-offset)]',
        )}
        style={{ '--reference-stack-expanded-width': `${expandedStackWidth}px` } as CSSProperties}
      >
        <div
          aria-label={titleText}
          className={cn(
            'group/card relative flex h-[82px] w-full shrink-0 items-center justify-center overflow-visible rounded-12 text-system-white transition-colors hover:z-20',
            !hasMedia && 'overflow-hidden rounded-12 border border-white/[0.12] bg-white/[0.16] hover:bg-white/[0.24]',
            cardIsClickable && 'cursor-pointer',
            disabled && 'cursor-not-allowed opacity-50',
          )}
          style={
            !hasMedia
              ? ({
                  backdropFilter: 'blur(100px)',
                  WebkitBackdropFilter: 'blur(100px)',
                } as CSSProperties)
              : undefined
          }
          onClick={cardIsClickable ? onClick : undefined}
        >
          {hasMedia ? (
            <>
              <div data-stack-hover-target={true} className="relative h-[82px] w-full shrink-0">
                <div className="hide-scrollbar pointer-events-none absolute top-0 left-0 h-full overflow-visible">
                  {hasMultipleMedia ? (
                    <div className="pointer-events-none h-px" style={{ width: `${expandedStackWidth}px` }} />
                  ) : null}
                  {chips.map((chip, index) => {
                    const stackStyle = stackLayerStyles[Math.min(index, stackLayerStyles.length - 1)];
                    const itemStyle = {
                      '--stack-top': stackStyle.top,
                      '--stack-left': stackStyle.left,
                      '--stack-height': stackStyle.height,
                      '--stack-width': stackStyle.width,
                      '--expanded-left': `${index * 72}px`,
                      zIndex: index + 1,
                    } as CSSProperties;

                    return (
                      <div
                        key={chip.id}
                        className={cn(
                          'group/chip absolute top-[var(--stack-top)] left-[var(--stack-left)] h-[var(--stack-height)] w-[var(--stack-width)] overflow-visible rounded-12 border border-white/[0.12] bg-white/[0.24] transition-all duration-200 ease-out',
                          hideCollapsedShadow
                            ? 'shadow-none'
                            : hasMultipleMedia && index === chips.length - 1
                              ? 'shadow-[0_0_10px_rgba(0,0,0,0.32)]'
                              : 'shadow-[0_6px_16px_rgba(0,0,0,0.24)]',
                          hasMultipleMedia &&
                            'pointer-events-auto group-data-[stack-expanded=true]/stack:top-0 group-data-[stack-expanded=true]/stack:left-[var(--expanded-left)] group-data-[stack-expanded=true]/stack:rounded-12 group-data-[stack-expanded=true]/stack:border-transparent group-data-[stack-expanded=true]/stack:shadow-[0_0_10px_rgba(0,0,0,0.32)]',
                        )}
                        style={itemStyle}
                      >
                        <MediaChipPreview chip={chip} className="rounded-12" textClassName="text-xs" />
                        {hasMultipleMedia && chip.removable && chip.onRemove ? (
                          <button
                            type="button"
                            aria-label={chip.label}
                            className="-top-1 -right-1 pointer-events-none absolute z-[60] flex size-4 shrink-0 items-center justify-center rounded-full bg-black/75 text-system-white80 opacity-0 backdrop-blur-[4px] transition-opacity hover:bg-black/90 hover:text-system-white group-hover/chip:pointer-events-auto group-hover/chip:opacity-100"
                            onClick={(event) => {
                              event.stopPropagation();
                              chip.onRemove?.();
                            }}
                          >
                            <X className="size-3" />
                          </button>
                        ) : null}
                        {hasMultipleMedia ? <ElementChipBadgeIcon chip={chip} /> : null}
                      </div>
                    );
                  })}
                  {showExpandedAddCard ? (
                    <MediaStackAddButton
                      ariaLabel={titleText}
                      className="pointer-events-none absolute top-0 left-[var(--add-collapsed-left)] flex h-[82px] w-[64px] cursor-pointer items-center justify-center rounded-12 border border-white/[0.12] bg-white/[0.16] text-system-white64 opacity-0 shadow-[0_0_10px_rgba(0,0,0,0.32)] transition-all duration-200 ease-out hover:border-white/[0.12] hover:bg-white/[0.16] hover:text-system-white disabled:cursor-not-allowed disabled:opacity-40 group-data-[stack-expanded=true]/stack:pointer-events-auto group-data-[stack-expanded=true]/stack:left-[var(--expanded-add-left)] group-data-[stack-expanded=true]/stack:opacity-100"
                      style={
                        {
                          '--add-collapsed-left': '0px',
                          '--expanded-add-left': `${chips.length * 72}px`,
                          zIndex: Math.min(chips.length + 1, 50),
                          backdropFilter: 'blur(100px)',
                          WebkitBackdropFilter: 'blur(100px)',
                        } as CSSProperties
                      }
                      onClick={onAddClick}
                      menuItems={addMenuItems}
                      onMenuOpenChange={handleAddMenuOpenChange}
                      disabled={addDisabled}
                    >
                      <Plus className="size-5" />
                    </MediaStackAddButton>
                  ) : null}
                </div>
              </div>
              {chips.length > 1 ? (
                <span className="-top-1 -right-1 pointer-events-none absolute z-40 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-black/75 px-1 font-medium text-[10px] text-system-white leading-none backdrop-blur transition-opacity group-data-[stack-expanded=true]/stack:opacity-0">
                  {chips.length}
                </span>
              ) : null}
              {removableChip ? (
                <button
                  type="button"
                  aria-label={removableChip.label}
                  data-stack-remove-button={true}
                  className="-top-1 -right-1 absolute z-[60] flex size-5 items-center justify-center rounded-full bg-black/75 text-system-white80 opacity-0 backdrop-blur-[4px] transition-opacity hover:bg-black/90 hover:text-system-white group-hover/card:pointer-events-auto group-hover/card:opacity-100"
                  onClick={(event) => {
                    event.stopPropagation();
                    removableChip.onRemove?.();
                  }}
                >
                  <X className="size-3" />
                </button>
              ) : null}
              {!hasMultipleMedia && chips[0] ? <ElementChipBadgeIcon chip={chips[0]} /> : null}
            </>
          ) : (
            (emptyContent ?? (
              <div className="flex flex-col items-center justify-center gap-1.5 text-system-white64 transition-colors group-hover/card:text-system-white">
                {emptyIcon}
                {showEmptyTitle ? (
                  <span className="max-w-full truncate px-1 text-center text-xs">{emptyTitle ?? title}</span>
                ) : null}
              </div>
            ))
          )}
          {showAddButton ? (
            <div className={cn('-translate-x-1/2 pointer-events-none absolute bottom-[-8px] left-1/2 z-50')}>
              <MediaStackAddButton
                ariaLabel={titleText}
                className={cn(
                  'pointer-events-auto flex h-5 cursor-pointer items-center justify-center rounded-[100px] bg-black/[0.56] px-2 text-system-white backdrop-blur-[4px] transition-colors hover:bg-black/[0.64] disabled:cursor-not-allowed disabled:opacity-40',
                  hasMultipleMedia &&
                    'opacity-100 group-data-[stack-expanded=true]/stack:pointer-events-none group-data-[stack-expanded=true]/stack:opacity-0',
                )}
                onClick={onAddClick}
                menuItems={addMenuItems}
                onMenuOpenChange={handleAddMenuOpenChange}
                disabled={addDisabled}
              >
                <Plus className="size-4" />
              </MediaStackAddButton>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export const HomeGenerationMultiFrameFrameCard = ({
  chip,
  compact = false,
}: {
  chip: MediaChip;
  index: number;
  compact?: boolean;
}) => (
  <div
    className={cn(
      'group/frame relative shrink-0 overflow-visible border border-white/[0.12] bg-white/[0.24] shadow-[0_10px_30px_rgba(0,0,0,0.24)]',
      'h-[82px] w-[64px] rounded-12',
    )}
  >
    <div className="absolute inset-0 overflow-hidden rounded-12">
      {chip.preview ? (
        <>
          <img src={chip.preview} alt={chip.label} className="size-full object-cover" />
          {chip.loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black/35 backdrop-blur-[1px]">
              <Loader2 className={cn('animate-spin text-system-white', compact ? 'size-4' : 'size-5')} />
            </div>
          ) : null}
        </>
      ) : chip.loading ? (
        <div className="flex size-full items-center justify-center bg-white/[0.04]">
          <Loader2 className={cn('animate-spin text-system-white64', compact ? 'size-4' : 'size-5')} />
        </div>
      ) : (
        <div className="flex size-full items-center justify-center bg-white/[0.04] text-system-white64">
          <TypeIcon className={compact ? 'size-4' : 'size-5'} />
        </div>
      )}
    </div>
    {chip.removable && chip.onRemove ? (
      <button
        type="button"
        aria-label={chip.label}
        className="-top-1 -right-1 absolute z-[60] flex size-4 shrink-0 items-center justify-center rounded-full bg-black/75 text-system-white80 opacity-0 backdrop-blur-[4px] transition-opacity hover:bg-black/90 hover:text-system-white group-hover/frame:pointer-events-auto group-hover/frame:opacity-100"
        onClick={(event) => {
          event.stopPropagation();
          chip.onRemove?.();
        }}
      >
        <X className="size-3" />
      </button>
    ) : null}
  </div>
);

export const HomeGenerationMultiFrameTransitionCard = ({
  compact = false,
  durationLabel,
  frameLabel,
  hasPrompt,
  open,
  onOpenChange,
  prompt,
  promptDraft,
  promptPlaceholder,
  onPromptDraftChange,
  onPromptConfirm,
  confirmLabel,
  cancelLabel,
  durationSelect,
  textareaRef,
}: {
  compact?: boolean;
  durationLabel: string;
  frameLabel: string;
  hasPrompt: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prompt: string;
  promptDraft: string;
  promptPlaceholder: string;
  onPromptDraftChange: (value: string) => void;
  onPromptConfirm: () => void;
  confirmLabel: string;
  cancelLabel: string;
  durationSelect: ReactNode;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
}) => {
  const isDraftChanged = promptDraft.trim() !== prompt.trim();

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [open, textareaRef]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={frameLabel}
          title={frameLabel}
          className={cn(
            'group flex shrink-0 flex-col items-center justify-center gap-1 border text-system-white transition-colors',
            'h-[82px] w-[64px] rounded-12 px-2',
            open
              ? 'border-white/[0.12] bg-white/[0.32]'
              : hasPrompt
                ? 'border-[#5765F2] bg-[#5765F2]/40 hover:border-[#5765F2] hover:bg-[#5765F2]/50'
                : 'border-white/[0.12] bg-white/[0.08] hover:border-white/[0.18] hover:bg-white/[0.12]',
          )}
        >
          <TypeIcon className={cn('size-5', !hasPrompt && !open && 'opacity-[0.48] group-hover:opacity-100')} />
          <span
            className={cn(
              'truncate font-medium leading-none',
              compact ? 'text-xs' : 'text-xs',
              !hasPrompt && !open && 'opacity-[0.48] group-hover:opacity-100',
            )}
          >
            {durationLabel}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="center"
        sideOffset={8}
        collisionPadding={16}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
        className="data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[41] w-[min(448px,calc(100vw-32px))] rounded-16 border border-white/[0.08] bg-[rgba(23,25,28,0.64)] p-3 text-system-white shadow-[0_24px_80px_rgba(0,0,0,0.4)] backdrop-blur-[30px] data-[state=closed]:animate-out data-[state=open]:animate-in"
      >
        <div className="space-y-3">
          <Textarea
            ref={textareaRef}
            value={promptDraft}
            onChange={(event) => onPromptDraftChange(event.target.value)}
            placeholder={promptPlaceholder}
            maxLength={MAX_PROMPT_LENGTH}
            className="min-h-[120px] resize-none rounded-none border-none bg-transparent p-0 text-sm text-system-white caret-system-blue02 placeholder:text-system-white28 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          <div className="flex items-end justify-between gap-3">
            <div className="relative z-50 shrink-0">{durationSelect}</div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-8 bg-white/[0.06] px-4 py-2 text-sm text-system-white64 transition-colors hover:bg-white/[0.08] hover:text-system-white"
                onClick={() => onOpenChange(false)}
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                disabled={!isDraftChanged}
                className="rounded-8 bg-white px-4 py-2 text-sm text-system-bg02 transition-colors hover:bg-white disabled:cursor-not-allowed disabled:bg-white/[0.08] disabled:text-system-white48"
                onClick={onPromptConfirm}
              >
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const HomeGenerationDesktopMediaArea = ({
  chips,
  title,
  emptyIcon,
  onClick,
  disabled,
  canAdd,
  onAddClick,
  addDisabled,
  addMenuItems,
  onAddMenuOpenChange,
  clickableWithMedia = false,
  showEmptyTitle = false,
  emptyTitle,
  emptyContent,
  expandedOffset,
  hideCollapsedShadow = false,
  stackExpanded,
  onStackExpandedChange,
}: {
  chips: MediaChip[];
  title: ReactNode;
  emptyIcon: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  canAdd?: boolean;
  onAddClick?: () => void;
  addDisabled?: boolean;
  addMenuItems?: MediaStackAddMenuItem[];
  onAddMenuOpenChange?: (open: boolean) => void;
  clickableWithMedia?: boolean;
  showEmptyTitle?: boolean;
  emptyTitle?: ReactNode;
  emptyContent?: ReactNode;
  expandedOffset?: DesktopMediaStackExpandedOffset;
  hideCollapsedShadow?: boolean;
  stackExpanded?: boolean;
  onStackExpandedChange?: (expanded: boolean) => void;
}) => (
  <DesktopMediaStackCard
    chips={chips}
    title={title}
    emptyIcon={emptyIcon}
    onClick={onClick}
    disabled={disabled}
    canAdd={canAdd}
    onAddClick={onAddClick}
    addDisabled={addDisabled}
    addMenuItems={addMenuItems}
    onAddMenuOpenChange={onAddMenuOpenChange}
    clickableWithMedia={clickableWithMedia}
    showEmptyTitle={showEmptyTitle}
    emptyTitle={emptyTitle}
    emptyContent={emptyContent}
    expandedOffset={expandedOffset}
    hideCollapsedShadow={hideCollapsedShadow}
    stackExpanded={stackExpanded}
    onStackExpandedChange={onStackExpandedChange}
  />
);

export const HomeGenerationMobileUploadTrigger = ({
  onClick,
  disabled,
  tone = 'dark',
}: {
  onClick?: () => void;
  disabled?: boolean;
  tone?: 'dark' | 'light';
}) => (
  <button
    type="button"
    className={cn(
      'flex size-16 shrink-0 items-center justify-center rounded-8 disabled:cursor-not-allowed',
      tone === 'light'
        ? 'bg-white/[0.04] text-system-white disabled:text-system-white24'
        : 'bg-white/[0.04] text-system-white disabled:text-system-white24',
    )}
    onClick={onClick}
    disabled={disabled}
  >
    <Plus className="size-5" />
  </button>
);
